/* eslint-disable import/order, simple-import-sort/imports */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { faBars, faSignOut, faSun, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faChevronCircleLeft, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isObject } from 'lodash';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/Dropdown';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FloatingScanButton from 'src/components/FloatingScanButton';
import SupportDropdown from 'src/components/SupportDropdown';
import { useCurrentUserRoles } from 'src/hooks/services/useUser';
import { CONTACTLESS_LOGIN_ENABLED, LOCALSTORAGE_KEYS } from 'src/utils/constants';
import routes from 'src/utils/routes';
import useLocalstorage from 'src/utils/useLocalStorage';
import { getShortUuid } from 'src/utils/url';
import { getAvatarName } from 'src/utils/avatar';

import SessionCountdown from './session/session-countdown';

const BackButton = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type='button'
    className='btn btn-sm btn-light text-primary rounded-start back-button'
    aria-label='Back'
    onClick={onClick}
  >
    <FontAwesomeIcon icon={faChevronCircleLeft} />
  </button>
));

BackButton.displayName = 'BackButton';

BackButton.propTypes = {
  onClick: PropTypes.func,
};

BackButton.defaultProps = {
  onClick: null,
};

const MAX_HISTORY_ITEMS = 20;

const Header = ({
  title,
  back,
  menu,
  user,
  scan,
  onPrintQRCodesClick,
  historyLabel,
  historyDetail,
  excludeFromHistory,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = title.startsWith('Authentise') ? title : `Authentise – ${title}`;
  const [navigationHistory, setNavigationHistory] = useLocalstorage(
    LOCALSTORAGE_KEYS.NAVIGATION_HISTORY,
    []
  );
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [cookies] = useCookies([CONTACTLESS_LOGIN_ENABLED]);

  const initialLogin = isObject(user) && 'initialLogin' in user;
  const hideBack = back === '' || initialLogin;

  const { data: userRoles } = useCurrentUserRoles(user);
  const isServiceProvider = userRoles?.includes('service-provider');
  const userName = user ? (isServiceProvider ? user.username : user.name) : null;

  const hasHistory =
    Array.isArray(navigationHistory) && navigationHistory.filter(Boolean).length > 1;

  const previousLocations = hasHistory
    ? navigationHistory.filter(Boolean).slice(0, -1).slice(-MAX_HISTORY_ITEMS).reverse()
    : [];

  const backHistoryRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const safeTitle = title || '';

    if (excludeFromHistory || safeTitle.startsWith('Loading') || safeTitle.includes('Not Found')) {
      return;
    }

    const path = `${location.pathname}${location.search || ''}`;

    let label = historyLabel || title || path;
    let detail = historyDetail || null;

    if (!historyDetail) {
      if (location.pathname.startsWith('/material-batch/')) {
        const [, , batchUuid] = location.pathname.split('/');
        if (batchUuid) {
          detail = `(${getShortUuid(batchUuid)})`;
        }
      } else if (location.pathname.startsWith('/permanent-container/')) {
        const [, , containerUuid] = location.pathname.split('/');
        if (containerUuid) {
          detail = `(${getShortUuid(containerUuid)})`;
        }
      }
    }

    setNavigationHistory(prevHistory => {
      const safeHistory = Array.isArray(prevHistory) ? prevHistory : [];
      const withoutCurrent = safeHistory.filter(entry => entry && entry.path !== path);

      const nextHistory = [
        ...withoutCurrent,
        {
          path,
          label,
          detail,
        },
      ];

      return nextHistory.slice(-MAX_HISTORY_ITEMS);
    });
  }, [
    location.pathname,
    location.search,
    title,
    historyLabel,
    historyDetail,
    excludeFromHistory,
    setNavigationHistory,
  ]);

  useEffect(() => {
    if (!isHistoryOpen) {
      return undefined;
    }

    const handleClickOutside = event => {
      if (!backHistoryRef.current) {
        return;
      }

      if (!backHistoryRef.current.contains(event.target)) {
        setIsHistoryOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isHistoryOpen]);

  // eslint-disable-next-line react/display-name, react/prop-types
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      id='dropdown-custom-components'
      type='button'
      tabIndex={0}
      className='avatar'
      data-cy='header-avatar-btn'
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </button>
  ));

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;

    const setHeaderHeight = () => {
      const height = Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--app-header-height', `${height}px`);
    };

    setHeaderHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', setHeaderHeight);
      return () => {
        window.removeEventListener('resize', setHeaderHeight);
        document.documentElement.style.removeProperty('--app-header-height');
      };
    }

    const observer = new ResizeObserver(setHeaderHeight);
    observer.observe(header);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--app-header-height');
    };
  }, [title, menu, userName, scan]);

  return (
    <>
      <header ref={headerRef} role='banner'>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <nav role='navigation' className='navbar navbar-dark bg-primary navbar-expand-sm'>
          <div ref={backHistoryRef} className='navbar-back-control position-relative'>
            {!hideBack && (
              <>
                <button
                  type='button'
                  className='nav-icon-btn'
                  aria-label='Go back'
                  data-cy='header-back-btn'
                  onClick={() => {
                    setIsHistoryOpen(false);
                    navigate(-1);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <button
                  type='button'
                  className='nav-icon-btn'
                  aria-label='Open navigation history'
                  aria-haspopup='true'
                  aria-expanded={isHistoryOpen}
                  disabled={!hasHistory}
                  data-cy='header-history-toggle-btn'
                  onClick={() => setIsHistoryOpen(o => !o)}
                >
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>

                {isHistoryOpen && hasHistory && (
                  <div className='back-history-dropdown dropdown-menu show'>
                    {previousLocations.map((entry, index) => (
                      <button
                        key={entry.path}
                        type='button'
                        className='dropdown-item'
                        data-cy={`header-history-item-${index}`}
                        onClick={() => {
                          setIsHistoryOpen(false);
                          navigate(entry.path);
                        }}
                      >
                        <span className='d-block text-truncate'>{entry.label}</span>

                        {entry.detail && (
                          <span className='d-block text-truncate small text-muted'>
                            {entry.detail}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <div
              className='navbar-text text-center w-100 text-break'
              data-testid='header-title'
              data-cy='header-title'
            >
              {title}
            </div>
          </div>

          <div className='d-flex align-items-center mr-3'>
            {!menu && user && cookies.contactlessLoginEnabled && <SessionCountdown />}

            <SupportDropdown />
          </div>
          <div className='navbar-square-item d-flex justify-content-center align-items-center'>
            {menu && (
              <Link to={menu}>
                <button
                  type='button'
                  className='btn text-reset text-white'
                  aria-label='Recent Activity'
                  data-cy='header-menu-btn'
                >
                  <FontAwesomeIcon icon={faBars} />
                </button>
              </Link>
            )}
            {!menu && user && (
              <Dropdown>
                <Dropdown.Toggle as={CustomToggle}>
                  <span aria-label={`${userName}`}>{getAvatarName(userName)}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item href={routes.about} data-cy='header-about-link'>
                    <FontAwesomeIcon icon={faSun} /> About Page
                  </Dropdown.Item>
                  <Dropdown.Item href={routes.logout} data-cy='header-logout-link'>
                    <FontAwesomeIcon icon={faSignOut} color='#000' /> Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </nav>
      </header>
      {!scan && !menu && user && <FloatingScanButton onPrintQRCodesClick={onPrintQRCodesClick} />}
    </>
  );
};

Header.propTypes = {
  onPrintQRCodesClick: PropTypes.func,
  title: PropTypes.string,
  back: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  menu: PropTypes.string,
  scan: PropTypes.bool,
  historyLabel: PropTypes.string,
  historyDetail: PropTypes.string,
  excludeFromHistory: PropTypes.bool,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
  }),
};

Header.defaultProps = {
  onPrintQRCodesClick: null,
  title: 'Authentise',
  back: () => null,
  menu: '',
  scan: false,
  historyLabel: null,
  historyDetail: null,
  excludeFromHistory: false,
  user: null,
};

export default Header;
